import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, UserCircle, ShieldCheck, MagnifyingGlass, Eye, EyeSlash, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const MODULO_LABEL = {
  pos: "Punto de Venta",
  dashboard: "Dashboard",
  plan_cuentas: "Plan de Cuentas · Sumas y Saldos",
  asientos: "Asientos contables",
  ventas: "Ventas · Facturación · Pedidos · REC · Reparto",
  productos: "Productos y Listas de Precios",
  clientes: "Ctas. Ctes. Clientes",
  tienda_nube: "Tienda Nube",
  compras: "Compras",
  insumos: "Insumos y M. Primas",
  proveedores: "Ctas. Ctes. Proveedores",
  cajas: "Cajas, Bancos y Cheques",
  cashflow: "CashFlow",
  admin_users: "Administrador de Usuarios",
  medios_pago: "Medios de Pago",
  auditoria: "Auditoría de Movimientos",
  auditoria_salida: "Auditoría de Salida",
  etiquetas: "Etiquetas",
  pasivos: "Pasivos",
  gastos_estructura: "Gastos de Estructura",
  // Sub-módulos de Producción (granulares)
  ordenes_trabajo: "Órdenes de Trabajo",
  recepcion_mercaderia: "Recepción de Mercadería",
  transformaciones: "Transformaciones",
  recetas: "Recetas y Combos",
  control_salida: "Control de Salida",
  inventario: "Inventario",
  stock: "Stock",
};

// Agrupación visual — cada grupo permite seleccionar/deseleccionar todos sus permisos
// de golpe o marcar hijos individuales.
const PERM_GROUPS = [
  { key: "op",    label: "Operación diaria",       perms: ["pos", "dashboard"] },
  { key: "cont",  label: "Contabilidad",           perms: ["plan_cuentas", "asientos"] },
  { key: "vent",  label: "Ventas y Clientes",      perms: ["ventas", "productos", "clientes", "tienda_nube"] },
  { key: "prod",  label: "Producción",             perms: [
    "ordenes_trabajo", "recepcion_mercaderia", "transformaciones",
    "recetas", "control_salida", "inventario", "stock",
  ] },
  { key: "compr", label: "Compras y Proveedores",  perms: ["compras", "insumos", "proveedores"] },
  { key: "tes",   label: "Tesorería",              perms: ["cajas", "cashflow"] },
  { key: "adm",   label: "Administración",         perms: ["admin_users", "medios_pago", "auditoria"] },
  { key: "aud",   label: "Auditoría",               perms: ["auditoria_salida"] },
];

const emptyForm = () => ({
  username: "", password: "", name: "", is_admin: false, permissions: [], permissions_readonly: [], unidad_negocio_default: "", lista_precios_default_id: "",
  dashboard_widgets_allowed: null,  // null = todos permitidos
  dashboard_tabs_allowed: null,     // null = todas las pestañas
});

export default function AdminUsersPage() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [pwVisible, setPwVisible] = useState({});
  const verPassword = async (u) => {
    try {
      const { data } = await api.get(`/users/${u.id}/password`);
      setPwVisible((s) => ({ ...s, [u.id]: data.disponible ? data.password : null }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo obtener la contraseña");
    }
  };
  const [modulos, setModulos] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [widgetCatalog, setWidgetCatalog] = useState([]);
  const [dashTabs, setDashTabs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [delTarget, setDelTarget] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetModulesList, setResetModulesList] = useState([]);
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [posListaId, setPosListaId] = useState("");
  const [posListaSaving, setPosListaSaving] = useState(false);

  const toggleModule = (key) => {
    const next = new Set(selectedModules);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedModules(next);
  };
  const selectAll = () => setSelectedModules(new Set(resetModulesList.map((m) => m.key)));
  const clearAll = () => setSelectedModules(new Set());

  const runReset = async () => {
    if (!resetPassword) { toast.error("Ingresá la contraseña"); return; }
    if (selectedModules.size === 0) { toast.error("Elegí al menos un módulo o marca TODOS"); return; }
    setResetting(true);
    try {
      const isAll = selectedModules.size === resetModulesList.length;
      const body = { password: resetPassword };
      if (!isAll) body.modules = Array.from(selectedModules);
      const { data } = await api.post("/admin/reset-all-data", body);
      const total = Object.values(data.cleared || {}).reduce(
        (a, v) => a + (typeof v === "number" ? v : 0), 0
      );
      const extras = (data.recalculado || []).length
        ? ` · Recalculado: ${data.recalculado.join(", ")}`
        : "";
      toast.success(`Reset ${data.mode} · ${total} documentos borrados${extras}`);
      setResetOpen(false);
      setResetPassword("");
      setSelectedModules(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al resetear");
    } finally {
      setResetting(false);
    }
  };

  const load = async () => {
    const [{ data: us }, { data: ms }, { data: rm }, { data: lps }, wc, cfg] = await Promise.all([
      api.get("/users"),
      api.get("/users/modulos"),
      api.get("/admin/reset-modules").catch(() => ({ data: [] })),
      api.get("/listas-precios").catch(() => ({ data: [] })),
      api.get("/dashboard/widget-catalog").catch(() => ({ data: { widgets: [] } })),
      api.get("/config/pos-lista").catch(() => ({ data: { lista_precios_id: "" } })),
    ]);
    api.get("/dashboard/tabs").then(({ data }) => setDashTabs(data?.tabs || [])).catch(() => {});
    setUsers(us);
    setModulos(ms);
    setResetModulesList(rm || []);
    setListasPrecios(lps || []);
    setWidgetCatalog(wc?.data?.widgets || []);
    setPosListaId(cfg?.data?.lista_precios_id || "");
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      username: u.username,
      password: "",  // vacío = no cambia
      name: u.name || "",
      is_admin: !!u.is_admin,
      permissions: u.permissions || [],
      permissions_readonly: u.permissions_readonly || [],
      unidad_negocio_default: u.unidad_negocio_default || "",
      lista_precios_default_id: u.lista_precios_default_id || "",
      dashboard_widgets_allowed: Array.isArray(u.dashboard_widgets_allowed) ? [...u.dashboard_widgets_allowed] : null,
      dashboard_tabs_allowed: Array.isArray(u.dashboard_tabs_allowed) ? [...u.dashboard_tabs_allowed] : null,
    });
    setOpen(true);
  };

  const togglePerm = (m) => {
    if (form.is_admin) return;  // admin tiene todos
    const has = form.permissions.includes(m);
    setForm({
      ...form,
      permissions: has ? form.permissions.filter((x) => x !== m) : [...form.permissions, m],
      // Si sacamos el permiso, también sacamos su marca de solo-lectura.
      permissions_readonly: has
        ? (form.permissions_readonly || []).filter((x) => x !== m)
        : (form.permissions_readonly || []),
    });
  };

  // Alterna la marca "solo lectura" de un permiso. Sólo tiene efecto si el
  // permiso está activado. Sin acceso al módulo, la marca no aplica.
  const toggleReadOnly = (m) => {
    if (form.is_admin) return;
    if (!form.permissions.includes(m)) return;
    const ro = form.permissions_readonly || [];
    setForm({
      ...form,
      permissions_readonly: ro.includes(m) ? ro.filter((x) => x !== m) : [...ro, m],
    });
  };

  const toggleGroup = (groupKey) => {
    if (form.is_admin) return;
    const g = PERM_GROUPS.find((x) => x.key === groupKey);
    if (!g) return;
    const gPerms = g.perms.filter((p) => modulos.includes(p));
    const allActive = gPerms.every((p) => form.permissions.includes(p));
    let next;
    if (allActive) {
      next = form.permissions.filter((p) => !gPerms.includes(p));
    } else {
      next = Array.from(new Set([...form.permissions, ...gPerms]));
    }
    setForm({ ...form, permissions: next });
  };

  const toggleAll = () => {
    if (form.is_admin) return;
    const all = form.permissions.length === modulos.length;
    setForm({ ...form, permissions: all ? [] : [...modulos] });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) return toast.error("El usuario es obligatorio");
    if (!editingId && form.password.length < 4) return toast.error("Contraseña mínima 4 caracteres");
    try {
      if (editingId) {
        const payload = {
          username: form.username,
          name: form.name,
          is_admin: form.is_admin,
          permissions: form.permissions,
          permissions_readonly: form.permissions_readonly || [],
          unidad_negocio_default: form.unidad_negocio_default || null,
          lista_precios_default_id: form.lista_precios_default_id || null,
          dashboard_widgets_allowed: form.dashboard_widgets_allowed,
          dashboard_tabs_allowed: form.dashboard_tabs_allowed,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", {
          username: form.username,
          password: form.password,
          name: form.name,
          is_admin: form.is_admin,
          permissions: form.permissions,
          permissions_readonly: form.permissions_readonly || [],
          unidad_negocio_default: form.unidad_negocio_default || null,
          lista_precios_default_id: form.lista_precios_default_id || null,
          dashboard_widgets_allowed: form.dashboard_widgets_allowed,
          dashboard_tabs_allowed: form.dashboard_tabs_allowed,
        });
        toast.success("Usuario creado");
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/users/${delTarget.id}`);
      toast.success("Usuario eliminado");
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.toLowerCase();
    return users.filter((u) =>
      u.username.toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  return (
    <>
      <Topbar
        title="Administrador de Usuarios"
        subtitle={`${users.length} usuario(s) · gestión de accesos y permisos por módulo`}
        right={
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#999]" />
              <Input
                data-testid="users-search"
                placeholder="Buscar usuario…"
                className="rounded-sm h-9 w-56 pl-8 text-[13px]"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Button
              data-testid="new-user-btn"
              onClick={openNew}
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9"
            >
              <Plus size={16} className="mr-1" /> Nuevo usuario
            </Button>
          </div>
        }
      />

      <div className="p-4 space-y-4">
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] grid-table">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="px-5 py-2 text-left w-16"></th>
                  <th className="px-5 py-2 text-left">Usuario</th>
                  <th className="px-5 py-2 text-left">Nombre</th>
                  <th className="px-5 py-2 text-left w-24">Rol</th>
                  <th className="px-5 py-2 text-left">Módulos habilitados</th>
                  <th className="px-5 py-2 text-right w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`user-${u.username}`} className="group hover:bg-neutral-50">
                    <td className="px-5 py-2 text-center">
                      {u.is_admin
                        ? <ShieldCheck size={22} weight="duotone" className="text-[#6B1F24] inline" />
                        : <UserCircle size={22} weight="duotone" className="text-neutral-500 inline" />}
                    </td>
                    <td className="px-5 py-2 font-mono font-semibold">
                      {u.username}
                      <div className="mt-0.5 flex items-center gap-1.5 font-normal" data-testid={`pw-cell-${u.username}`}>
                        {pwVisible[u.id] !== undefined ? (
                          pwVisible[u.id] ? (
                            <>
                              <span className="text-[12px] font-mono bg-amber-50 border border-amber-200 text-amber-900 rounded-sm px-1.5 py-0.5 select-all" data-testid={`pw-value-${u.username}`}>{pwVisible[u.id]}</span>
                              <button type="button" onClick={() => { navigator.clipboard?.writeText(pwVisible[u.id]); toast.success("Contraseña copiada"); }}
                                className="text-neutral-500 hover:text-[#6B1F24]" title="Copiar" data-testid={`pw-copy-${u.username}`}><Copy size={13} /></button>
                              <button type="button" onClick={() => setPwVisible((s) => { const n = { ...s }; delete n[u.id]; return n; })}
                                className="text-neutral-500 hover:text-[#6B1F24]" title="Ocultar" data-testid={`pw-hide-${u.username}`}><EyeSlash size={13} /></button>
                            </>
                          ) : (
                            <span className="text-[10px] text-amber-700" data-testid={`pw-na-${u.username}`}>No disponible: reseteá la contraseña una vez para que quede visible.</span>
                          )
                        ) : (
                          <button type="button" onClick={() => verPassword(u)} data-testid={`pw-show-${u.username}`}
                            className="inline-flex items-center gap-1 text-[10px] text-neutral-500 hover:text-[#6B1F24]" title="Ver contraseña configurada (queda auditado)">
                            <Eye size={13} /> ver contraseña
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-2 text-[#666]">{u.name || "-"}</td>
                    <td className="px-5 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-mono ${
                        u.is_admin ? "bg-[#6B1F24] text-white" : "bg-neutral-100 text-neutral-700"
                      }`}>
                        {u.is_admin ? "Admin" : "Usuario"}
                      </span>
                    </td>
                    <td className="px-5 py-2">
                      {u.is_admin ? (
                        <span className="text-[11px] font-mono text-[#666]">Todos los módulos</span>
                      ) : u.permissions.length === 0 ? (
                        <span className="text-[11px] font-mono text-[#B84A52]">Sin permisos</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {u.permissions.map((p) => (
                            <span key={p} className="text-[10px] font-mono bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded-sm">
                              {MODULO_LABEL[p] || p}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        <button
                          data-testid={`edit-user-${u.username}`}
                          onClick={() => openEdit(u)}
                          className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                        >
                          <PencilSimple size={14} />
                        </button>
                        {u.id !== current?.id && (
                          <button
                            data-testid={`del-user-${u.username}`}
                            onClick={() => setDelTarget(u)}
                            className="p-1.5 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[#999]">Sin usuarios.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Configuración de listas de precios global */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm p-5" data-testid="pos-lista-config">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="font-heading text-sm font-bold text-[#6B1F24] uppercase tracking-wide">
                Configuración global · Lista de precios del POS
              </div>
              <div className="text-[12px] text-[#666] mt-2 leading-relaxed">
                Elegí qué lista usa por defecto el POS (típicamente "Minorista"). Los operadores pueden cambiarla temporalmente por venta desde el dropdown de listas en el POS; al terminar cada venta vuelve automáticamente al default de acá.<br />
                <span className="text-[11px] font-mono">Los pedidos de Tienda Nube / WhatsApp usan la lista marcada como <b>es_lista_tn</b>. La facturación normal usa la lista del cliente.</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={posListaId || ""}
                onChange={(e) => setPosListaId(e.target.value)}
                className="h-9 px-2 rounded-sm border border-[#E5E5E5] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#6B1F24]/30 min-w-[220px]"
                data-testid="pos-lista-select"
              >
                <option value="">(Primera lista disponible)</option>
                {listasPrecios.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.nombre}{l.es_lista_tn ? " · TN" : ""}
                  </option>
                ))}
              </select>
              <Button
                disabled={posListaSaving}
                onClick={async () => {
                  setPosListaSaving(true);
                  try {
                    await api.put("/config/pos-lista", { lista_precios_id: posListaId || null });
                    toast.success("Lista POS actualizada");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Error al guardar");
                  } finally { setPosListaSaving(false); }
                }}
                className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9"
                data-testid="pos-lista-save"
              >
                Guardar
              </Button>
            </div>
          </div>
        </div>

        {/* Zona de peligro — Reset MODULAR de datos */}
        <div className="border border-red-300 bg-red-50 rounded-sm p-5 space-y-3" data-testid="reset-danger-zone">
          <div className="flex items-start gap-3">
            <div className="text-red-700 font-heading text-sm font-bold uppercase tracking-wide">
              ⚠ Zona de peligro
            </div>
          </div>
          <div className="text-[13px] text-red-900">
            <b>Reset selectivo por módulos.</b> Podés elegir qué módulos borrar. Al resetear un módulo,
            el sistema también revierte los efectos que ese módulo generó en otras áreas (ej: borrar Ventas
            elimina las facturas, los cobros asociados, los movimientos de caja generados y recalcula los saldos
            de cajas y clientes). Preserva usuarios, caché SENASA y tokens AFIP. Requiere contraseña específica.
          </div>
          <div>
            <Button
              variant="outline"
              className="border-red-500 text-red-700 hover:bg-red-100 rounded-sm"
              onClick={() => setResetOpen(true)}
              data-testid="reset-all-data-btn"
            >
              <Trash size={16} className="mr-2" /> Resetear datos (selectivo)
            </Button>
          </div>
        </div>
      </div>

      {/* Diálogo de confirmación de reset MODULAR */}
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent className="max-w-2xl" data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <Trash size={18} /> Reset selectivo de datos
            </AlertDialogTitle>
            <AlertDialogDescription>
              Marcá los módulos que querés resetear. El sistema también revierte automáticamente los movimientos
              que esos módulos generaron en otros lugares (cajas, cuentas corrientes, etc.).
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex items-center justify-between text-[12px] mb-2">
            <span className="text-neutral-500">
              {selectedModules.size} de {resetModulesList.length} módulos seleccionados
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={selectAll} className="text-[11px] text-red-700 underline">Seleccionar todos</button>
              <span className="text-neutral-300">·</span>
              <button type="button" onClick={clearAll} className="text-[11px] text-neutral-500 underline">Limpiar</button>
            </div>
          </div>

          <div className="border border-red-200 rounded max-h-[320px] overflow-y-auto">
            {resetModulesList.map((m) => {
              const checked = selectedModules.has(m.key);
              return (
                <label
                  key={m.key}
                  className={`flex items-start gap-3 px-3 py-2 border-b border-red-100 last:border-b-0 cursor-pointer hover:bg-red-50 ${
                    checked ? "bg-red-100" : "bg-white"
                  }`}
                  data-testid={`reset-module-${m.key}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleModule(m.key)}
                    className="mt-1 accent-red-600"
                    data-testid={`reset-check-${m.key}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-neutral-800">{m.label}</div>
                    {m.recalcula && m.recalcula.length > 0 && (
                      <div className="text-[10px] font-mono uppercase text-amber-700 mt-0.5">
                        ⟳ Recalcula: {m.recalcula.join(", ")}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          <div className="mt-3">
            <Label className="text-[11px] text-neutral-600">Contraseña de seguridad</Label>
            <div className="relative mt-1">
              <Input
                type={showResetPassword ? "text" : "password"}
                placeholder="Contraseña"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="rounded-sm font-mono pr-10"
                data-testid="reset-password-input"
                autoFocus
              />
              <button
                type="button"
                data-testid="toggle-reset-password"
                onClick={() => setShowResetPassword((v) => !v)}
                aria-label={showResetPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#666] hover:text-[#1A1A1A]"
                tabIndex={-1}
              >
                {showResetPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setResetPassword(""); setSelectedModules(new Set()); }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={resetting || selectedModules.size === 0}
              onClick={runReset}
              data-testid="reset-confirm-btn"
            >
              {resetting ? "Borrando…" : `Resetear ${selectedModules.size} módulo${selectedModules.size === 1 ? "" : "s"}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sheet crear/editar */}
      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
        <SheetContent className="w-full sm:w-[520px] sm:max-w-[520px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {editingId ? "Editar usuario" : "Nuevo usuario"}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={save} className="space-y-4 mt-6">
            <div>
              <Label className="text-[12px]">Usuario</Label>
              <Input
                data-testid="user-username"
                required
                className="rounded-sm mt-1 font-mono"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="ej: juan, contadora, mostrador1"
              />
              <div className="text-[10px] text-[#999] mt-1 font-mono">
                Puede ser cualquier texto, no hace falta que sea un email.
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Nombre / Descripción</Label>
              <Input
                className="rounded-sm mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre real o descripción del puesto"
              />
            </div>
            <div>
              <Label className="text-[12px]">
                Contraseña {editingId && <span className="text-[10px] text-[#999]">(dejá vacío para no cambiarla)</span>}
              </Label>
              <div className="relative mt-1">
                <Input
                  data-testid="user-password"
                  type={showFormPassword ? "text" : "password"}
                  className="rounded-sm font-mono pr-10"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required={!editingId}
                  minLength={editingId ? 0 : 4}
                />
                <button
                  type="button"
                  data-testid="toggle-user-password"
                  onClick={() => setShowFormPassword((v) => !v)}
                  aria-label={showFormPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#666] hover:text-[#1A1A1A]"
                  tabIndex={-1}
                >
                  {showFormPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="border border-[#E5E5E5] rounded-sm p-3 bg-white">
              <Label htmlFor="user-unidad-negocio" className="text-[11px] uppercase font-mono text-[#666] tracking-widest">
                Unidad de negocio por defecto
              </Label>
              <select
                id="user-unidad-negocio"
                data-testid="user-unidad-negocio"
                value={form.unidad_negocio_default || ""}
                onChange={(e) => setForm({ ...form, unidad_negocio_default: e.target.value })}
                className="mt-1 w-full h-9 px-2 rounded-sm border border-[#E5E5E5] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#6B1F24]/30"
              >
                <option value="">(Sin predeterminada — el usuario elige al vender)</option>
                <option value="Mostrador">Mostrador</option>
                <option value="Logistica">Logística</option>
                <option value="Mayorista">Mayorista</option>
              </select>
              <div className="text-[10px] text-[#666] font-mono mt-1">
                Se precarga al abrir Nueva Venta / Nueva Factura.
              </div>
            </div>

            <div className="border border-[#E5E5E5] rounded-sm p-3 bg-white">
              <Label className="text-[11px] uppercase font-mono text-[#666] tracking-widest">
                Lista de precios · Facturación
              </Label>
              <div className="mt-1 text-[11px] text-[#666] font-mono bg-neutral-50 border border-neutral-200 rounded-sm p-2">
                Ya no se puede fijar una lista personal por usuario. Regla actual:
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  <li><b>POS</b> → usa la lista configurada como "POS" (Minorista).</li>
                  <li><b>Pedidos TN / WhatsApp</b> → usa la lista con flag <span className="font-bold">es_lista_tn</span>.</li>
                  <li><b>Facturación normal</b> → usa la lista del cliente.</li>
                </ul>
              </div>
            </div>

            <div className="border border-[#E5E5E5] rounded-sm p-3 flex items-center justify-between bg-[#F5F1E9]">
              <div>
                <div className="text-[13px] font-semibold flex items-center gap-2">
                  <ShieldCheck size={16} weight="duotone" className="text-[#6B1F24]" />
                  Administrador
                </div>
                <div className="text-[11px] text-[#666] mt-0.5">
                  Los admins tienen acceso a todos los módulos automáticamente.
                </div>
              </div>
              <Switch
                data-testid="user-is-admin"
                checked={form.is_admin}
                onCheckedChange={(v) => setForm({
                  ...form,
                  is_admin: v,
                  permissions: v ? [...modulos] : form.permissions,
                })}
              />
            </div>

            <div className="border border-[#E5E5E5] rounded-sm">
              <div className="px-3 py-2 bg-neutral-50 flex items-center justify-between border-b">
                <span className="text-[11px] uppercase font-mono text-[#666] tracking-widest">
                  Módulos habilitados
                </span>
                {!form.is_admin && (
                  <button type="button" onClick={toggleAll}
                    className="text-[11px] text-[#6B1F24] hover:underline font-mono">
                    {form.permissions.length === modulos.length ? "Ninguno" : "Todos"}
                  </button>
                )}
              </div>
              <div className="divide-y divide-[#E5E5E5]">
                {PERM_GROUPS.map((g) => {
                  const gPerms = g.perms.filter((p) => modulos.includes(p));
                  if (gPerms.length === 0) return null;
                  const activeCount = gPerms.filter(
                    (p) => form.is_admin || form.permissions.includes(p)
                  ).length;
                  const allActive = activeCount === gPerms.length;
                  const someActive = activeCount > 0 && !allActive;
                  return (
                    <div key={g.key} data-testid={`perm-group-${g.key}`}>
                      {/* Header del grupo con checkbox tri-state */}
                      <label
                        className={`flex items-center gap-3 px-3 py-2 bg-[#F5F1E9]/60 hover:bg-[#F5F1E9] cursor-pointer ${
                          form.is_admin ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                        data-testid={`perm-group-toggle-${g.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={allActive}
                          ref={(el) => { if (el) el.indeterminate = someActive; }}
                          disabled={form.is_admin}
                          onChange={() => toggleGroup(g.key)}
                          className="accent-[#6B1F24]"
                        />
                        <span className="flex-1 text-[12px] font-heading font-semibold text-[#6B1F24] uppercase tracking-wide">
                          {g.label}
                        </span>
                        <span className="text-[10px] font-mono text-[#999]">
                          {activeCount}/{gPerms.length}
                        </span>
                      </label>
                      {/* Hijos individuales */}
                      <div className="pl-8 pr-3 py-2 grid grid-cols-1 gap-1 bg-white">
                        {gPerms.map((m) => {
                          const active = form.is_admin || form.permissions.includes(m);
                          const readOnly = (form.permissions_readonly || []).includes(m);
                          return (
                            <div
                              key={m}
                              data-testid={`perm-${m}`}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-[12px] transition-colors ${
                                active
                                  ? readOnly
                                    ? "border-amber-500 bg-amber-50 text-amber-900"
                                    : "border-[#6B1F24] bg-[#F5F1E9] text-[#6B1F24]"
                                  : "border-[#E5E5E5] hover:bg-neutral-50"
                              } ${form.is_admin ? "opacity-70" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                disabled={form.is_admin}
                                onChange={() => togglePerm(m)}
                                className="accent-[#6B1F24] cursor-pointer"
                              />
                              <span className="font-mono flex-1 cursor-pointer" onClick={() => !form.is_admin && togglePerm(m)}>
                                {MODULO_LABEL[m] || m}
                              </span>
                              {active && !form.is_admin && (
                                <button
                                  type="button"
                                  onClick={() => toggleReadOnly(m)}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase transition-colors ${
                                    readOnly
                                      ? "bg-amber-500 border-amber-600 text-white hover:bg-amber-600"
                                      : "bg-white border-neutral-300 text-neutral-500 hover:border-amber-500 hover:text-amber-700"
                                  }`}
                                  data-testid={`perm-ro-${m}`}
                                  title={readOnly
                                    ? "Solo lectura ACTIVO — este usuario NO puede cargar cambios en este módulo. Click para permitir escritura."
                                    : "Marcar como solo lectura — el usuario podrá ver la pestaña pero no cargar/editar/borrar."}
                                >
                                  {readOnly ? "🔒 Solo lectura" : "Lectura + Escritura"}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {/* Fallback: cualquier permiso del backend que NO figure en ningún grupo */}
                {(() => {
                  const knownPerms = new Set(PERM_GROUPS.flatMap((g) => g.perms));
                  const orphans = modulos.filter((m) => !knownPerms.has(m));
                  if (orphans.length === 0) return null;
                  return (
                    <div className="px-3 py-2">
                      <div className="text-[10px] font-mono uppercase text-[#999] mb-2">Otros</div>
                      <div className="grid grid-cols-1 gap-1">
                        {orphans.map((m) => {
                          const active = form.is_admin || form.permissions.includes(m);
                          const readOnly = (form.permissions_readonly || []).includes(m);
                          return (
                            <div
                              key={m}
                              data-testid={`perm-${m}`}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-[12px] transition-colors ${
                                active
                                  ? readOnly
                                    ? "border-amber-500 bg-amber-50 text-amber-900"
                                    : "border-[#6B1F24] bg-[#F5F1E9] text-[#6B1F24]"
                                  : "border-[#E5E5E5] hover:bg-neutral-50"
                              } ${form.is_admin ? "opacity-70" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                disabled={form.is_admin}
                                onChange={() => togglePerm(m)}
                                className="accent-[#6B1F24] cursor-pointer"
                              />
                              <span className="font-mono flex-1 cursor-pointer" onClick={() => !form.is_admin && togglePerm(m)}>
                                {MODULO_LABEL[m] || m}
                              </span>
                              {active && !form.is_admin && (
                                <button
                                  type="button"
                                  onClick={() => toggleReadOnly(m)}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase transition-colors ${
                                    readOnly
                                      ? "bg-amber-500 border-amber-600 text-white hover:bg-amber-600"
                                      : "bg-white border-neutral-300 text-neutral-500 hover:border-amber-500 hover:text-amber-700"
                                  }`}
                                  data-testid={`perm-ro-${m}`}
                                >
                                  {readOnly ? "🔒 Solo lectura" : "Lectura + Escritura"}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <Button
              data-testid="save-user-btn"
              type="submit"
              className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
            >
              {editingId ? "Guardar cambios" : "Crear usuario"}
            </Button>
          </form>

          {/* Widgets del Dashboard visibles para este usuario. Sólo aplica si
              tiene el permiso "dashboard" (o si es admin la lista se ignora en
              backend). Null/no restringido = ve todos los widgets del catálogo. */}
          {!form.is_admin && dashTabs.length > 0 && (
            <div className="mt-6 border border-[#E5E5E5] rounded-sm bg-white" data-testid="user-tabs-section">
              <div className="px-3 py-2 border-b bg-neutral-50 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[13px] font-heading font-semibold text-[#6B1F24]">Pestañas del Dashboard visibles</div>
                  <div className="text-[10px] font-mono text-[#666] mt-0.5">
                    Las pestañas las crean y configuran los administradores. Elegí cuáles ve este usuario (ideal para reuniones de equipo: una sola pestaña).
                  </div>
                  {!(form.permissions || []).includes("dashboard") && (
                    <div className="text-[10px] font-mono text-amber-700 mt-1" data-testid="user-tabs-no-dashboard-perm">
                      Este usuario no tiene el módulo Dashboard habilitado: al elegir una pestaña se habilita automáticamente para que le aparezca el botón.
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setForm({ ...form, dashboard_tabs_allowed: null })}
                    className={`text-[11px] font-mono px-2 py-1 rounded-sm border ${form.dashboard_tabs_allowed === null ? "bg-[#6B1F24] text-white border-[#6B1F24]" : "bg-white border-neutral-300"}`}
                    data-testid="user-tabs-all">Todas</button>
                  <button type="button" onClick={() => setForm({ ...form, dashboard_tabs_allowed: [] })}
                    className={`text-[11px] font-mono px-2 py-1 rounded-sm border ${Array.isArray(form.dashboard_tabs_allowed) ? "bg-[#6B1F24] text-white border-[#6B1F24]" : "bg-white border-neutral-300"}`}
                    data-testid="user-tabs-restrict">Elegir</button>
                </div>
              </div>
              {Array.isArray(form.dashboard_tabs_allowed) ? (
                <div className="p-3 flex flex-wrap gap-2">
                  {dashTabs.map((t) => {
                    const on = form.dashboard_tabs_allowed.includes(t.id);
                    return (
                      <button key={t.id} type="button" data-testid={`user-tab-toggle-${t.id}`}
                        onClick={() => {
                          const next = on ? form.dashboard_tabs_allowed.filter((x) => x !== t.id) : [...form.dashboard_tabs_allowed, t.id];
                          const perms = form.permissions || [];
                          setForm({ ...form, dashboard_tabs_allowed: next, permissions: next.length > 0 && !perms.includes("dashboard") ? [...perms, "dashboard"] : perms });
                        }}
                        className={`px-3 py-1.5 rounded-sm border text-[12px] font-mono uppercase tracking-wider ${on ? "bg-[#6B1F24] text-white border-[#6B1F24]" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"}`}>
                        {t.nombre} <span className="opacity-70 normal-case tracking-normal">· {(t.widget_keys || []).length} ind.</span>
                      </button>
                    );
                  })}
                  {form.dashboard_tabs_allowed.length === 0 && <div className="w-full text-[11px] font-mono text-amber-700">Sin pestañas: el usuario no verá ningún indicador.</div>}
                </div>
              ) : (
                <div className="p-4 text-center text-[11px] font-mono text-neutral-500">Ve todas las pestañas ({dashTabs.length}). Tocá "Elegir" para limitar.</div>
              )}
            </div>
          )}

          {!form.is_admin && (form.permissions || []).includes("dashboard") && widgetCatalog.length > 0 && (
            <div className="mt-6 border border-[#E5E5E5] rounded-sm bg-white" data-testid="user-widgets-section">
              <div className="px-3 py-2 border-b bg-neutral-50 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[13px] font-heading font-semibold text-[#6B1F24]">Indicadores del Dashboard visibles</div>
                  <div className="text-[10px] font-mono text-[#666] mt-0.5">
                    Elegí qué widgets podrá ver este usuario. Si no restringís nada, verá todos los que tenga configurados en sus solapas.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, dashboard_widgets_allowed: null })}
                    className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm border transition-colors ${
                      form.dashboard_widgets_allowed === null
                        ? "bg-[#6B1F24] text-white border-[#6B1F24]"
                        : "bg-white text-neutral-600 border-neutral-300 hover:bg-neutral-50"
                    }`}
                    data-testid="user-widgets-todos"
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, dashboard_widgets_allowed: [] })}
                    className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm border transition-colors ${
                      Array.isArray(form.dashboard_widgets_allowed)
                        ? "bg-amber-600 text-white border-amber-600"
                        : "bg-white text-neutral-600 border-neutral-300 hover:bg-neutral-50"
                    }`}
                    data-testid="user-widgets-restringir"
                  >
                    Restringir
                  </button>
                </div>
              </div>
              {Array.isArray(form.dashboard_widgets_allowed) ? (
                <div className="p-3 space-y-3 max-h-[300px] overflow-y-auto">
                  {Object.entries(
                    widgetCatalog.reduce((acc, w) => {
                      (acc[w.group || "General"] ||= []).push(w);
                      return acc;
                    }, {})
                  ).map(([grp, ws]) => (
                    <div key={grp}>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1">{grp}</div>
                      <div className="grid grid-cols-2 gap-1">
                        {ws.map((w) => {
                          const active = (form.dashboard_widgets_allowed || []).includes(w.key);
                          return (
                            <label
                              key={w.key}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-[11px] cursor-pointer transition-colors ${
                                active ? "border-[#6B1F24] bg-[#F5F1E9] text-[#6B1F24]" : "border-neutral-200 hover:bg-neutral-50"
                              }`}
                              data-testid={`user-widget-${w.key}`}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => {
                                  const cur = form.dashboard_widgets_allowed || [];
                                  const next = active ? cur.filter((k) => k !== w.key) : [...cur, w.key];
                                  setForm({ ...form, dashboard_widgets_allowed: next });
                                }}
                                className="accent-[#6B1F24]"
                              />
                              <span className="font-mono">{w.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="text-[10px] font-mono text-neutral-500 pt-1 border-t border-neutral-100">
                    {(form.dashboard_widgets_allowed || []).length} de {widgetCatalog.length} widgets tildados.
                    {(form.dashboard_widgets_allowed || []).length === 0 && (
                      <span className="text-amber-700 ml-1">· Este usuario no verá ningún indicador.</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center text-[11px] font-mono text-neutral-500">
                  Acceso completo al catálogo de {widgetCatalog.length} widgets. Tocá "Restringir" para elegir manualmente.
                </div>
              )}
              <div className="px-3 py-2 border-t bg-neutral-50 text-right">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.put(`/users/${editingId}`, { dashboard_widgets_allowed: form.dashboard_widgets_allowed });
                      toast.success("Widgets guardados");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Error al guardar widgets");
                    }
                  }}
                  disabled={!editingId}
                  className="text-[11px] font-mono px-3 py-1 rounded-sm bg-[#6B1F24] text-white hover:bg-[#571519] disabled:opacity-30"
                  data-testid="user-widgets-save-quick"
                >
                  Guardar widgets
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar al usuario <span className="font-mono font-semibold">{delTarget?.username}</span>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-del-user"
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
